<template>
  <div class="ocr-report-header-map">
    <OcrTabs v-model="headerTab" class="ocr-report-header-subtabs">
      <v-tab value="fields">Properties</v-tab>
      <v-tab value="keywords">Keywords</v-tab>
    </OcrTabs>

    <template v-if="headerTab === 'fields'">
      <div class="ocr-report-map-toolbar">
        <v-btn size="small" color="primary" variant="flat" :loading="finding" @click="findKeywords">
          Find header values
        </v-btn>
        <v-btn size="small" variant="outlined" :loading="saving" @click="saveHeaders">
          Save header mapping
        </v-btn>
      </div>
      <div v-if="loading" class="ocr-empty-cell">Loading header fields…</div>
      <div v-else-if="!fields.length" class="ocr-empty-cell">
        No header fields for this table type. Set table type first.
      </div>
      <OcrTableWrap v-else title="Header properties" class="ocr-report-column-map-table">
        <v-table class="well-table sample-table well-table-data fd-table">
          <thead>
            <tr class="main-header">
              <th>Standard property</th>
              <th>Report value</th>
              <th>Report UOM</th>
              <th>Standard UOM</th>
            </tr>
          </thead>
          <tbody class="well-table-content">
            <tr v-for="(row, idx) in fields" :key="row.field_name + idx">
              <td>
                {{ row.field_name }}<span v-if="row.required" class="ocr-required-mark">*</span>
                <span v-if="row.predicted_value && row.value === row.predicted_value" title="Matches prediction"> ✨</span>
              </td>
              <td>
                <v-select
                  v-if="row.field_name === 'fluidSampleID' && sampleIds.length"
                  v-model="row.value"
                  :items="sampleIds"
                  :menu-props="selectMenuProps"
                  item-title="label"
                  item-value="value"
                  density="compact"
                  variant="outlined"
                  hide-details
                  class="ocr-report-column-select"
                />
                <v-select
                  v-else-if="row.field_name === 'fluidSampleContainerID' && sampleContainers.length"
                  v-model="row.value"
                  :items="sampleContainers"
                  :menu-props="selectMenuProps"
                  item-title="label"
                  item-value="value"
                  density="compact"
                  variant="outlined"
                  hide-details
                  class="ocr-report-column-select"
                />
                <input v-else v-model="row.value" class="ocr-editable-grid-input" type="text" />
              </td>
              <td>
                <input
                  v-if="row.has_hdr_uom && !row.enumeration_options?.length"
                  v-model="row.uom"
                  class="ocr-editable-grid-input"
                  type="text"
                />
                <span v-else class="ocr-empty-cell">—</span>
              </td>
              <td>
                <v-select
                  v-if="row.has_hdr_uom && row.uom_options?.length"
                  v-model="row.mapped_uom"
                  :items="row.uom_options"
                  :menu-props="selectMenuProps"
                  density="compact"
                  variant="outlined"
                  hide-details
                  class="ocr-report-column-select"
                />
                <span v-else class="ocr-empty-cell">—</span>
              </td>
            </tr>
          </tbody>
        </v-table>
      </OcrTableWrap>
    </template>

    <OcrReportHeaderKeywords
      v-else
      :filename="filename"
      :table-index="tableIndex"
      @updated="$emit('updated')"
      @status="$emit('status', $event)"
    />
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import OcrReportHeaderKeywords from "@/components/layout/OCR/OcrReportHeaderKeywords.vue";
import OcrTabs from "@/components/layout/OCR/OcrTabs.vue";
import OcrTableWrap from "@/components/layout/OCR/OcrTableWrap.vue";

export default {
  name: "OcrReportHeaderMap",
  components: { OcrReportHeaderKeywords, OcrTabs, OcrTableWrap },
  props: {
    filename: { type: String, default: null },
    tableIndex: { type: Number, default: null },
    tableType: { type: String, default: "" },
    listRevision: { type: Number, default: 0 },
  },
  emits: ["updated", "status"],
  data() {
    return {
      headerTab: "fields",
      loading: false,
      saving: false,
      finding: false,
      fields: [],
      sampleIds: [],
      sampleContainers: [],
      loadToken: 0,
      selectMenuProps: { contentClass: "ocr-select-menu" },
    };
  },
  watch: {
    filename: { handler() { this.load(); } },
    tableIndex: { handler() { this.load(); } },
    tableType() { this.load(); },
    listRevision() { this.load(); },
  },
  mounted() {
    this.load();
  },
  methods: {
    applyPayload(data) {
      this.fields = (data.fields || []).map((f) => ({ ...f }));
      this.sampleIds = data.samples?.fluid_sample_ids || [];
      this.sampleContainers = data.samples?.fluid_sample_containers || [];
    },
    async load() {
      const token = this.loadToken + 1;
      this.loadToken = token;
      this.fields = [];
      if (!this.filename || this.tableIndex == null) return;
      this.loading = true;
      try {
        const { data } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/headers`,
        );
        if (token !== this.loadToken) return;
        this.applyPayload(data);
      } catch (e) {
        if (token !== this.loadToken) return;
        this.$emit("status", { type: "error", message: formatOcrApiError(e, "Could not load header mapping.") });
      } finally {
        if (token === this.loadToken) this.loading = false;
      }
    },
    buildUpdates() {
      return this.fields.map((f) => ({
        field_name: f.field_name,
        value: f.value,
        uom: f.uom,
        mapped_uom: f.mapped_uom,
      }));
    },
    async saveHeaders() {
      this.saving = true;
      try {
        const { data } = await ocrApi.put(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/headers`,
          { fields: this.buildUpdates() },
        );
        this.applyPayload(data);
        this.$emit("status", { type: "success", message: "Header mapping saved." });
        this.$emit("updated");
      } catch (e) {
        this.$emit("status", { type: "error", message: formatOcrApiError(e, "Could not save header mapping.") });
      } finally {
        this.saving = false;
      }
    },
    async findKeywords() {
      this.finding = true;
      try {
        const { data } = await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/headers/find-keywords`,
        );
        this.applyPayload(data);
        this.$emit("status", { type: "success", message: "Header values extracted from page keywords." });
        this.$emit("updated");
      } catch (e) {
        this.$emit("status", { type: "error", message: formatOcrApiError(e, "Find header values failed.") });
      } finally {
        this.finding = false;
      }
    },
  },
};
</script>
